import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadGoogleMaps, MapsLoaderError } from '../../utils/mapsLoader';
import { parsePlace } from '../../utils/placeParser';

/**
 * Shared address picker used by the Host Business Profile and Client Brand forms.
 *
 * Sub-task 5.1 (scaffold): loader + skeleton + plain-input fallback.
 * Sub-task 5.2: Places Autocomplete + selection handling.
 *   - Debounced `AutocompleteService.getPlacePredictions` (session-tokened).
 *   - Keyboard + mouse prediction selection.
 *   - `PlacesService.getDetails` on a hidden `<div>` to populate fields.
 *   - `onChange(value, extras?)` — extras only in `structured` mode.
 *   - Inline error on API failure; input stays editable.
 * Sub-task 5.3 (this file): interactive map + draggable marker.
 *   - `google.maps.Map` rendered below the input, sized `h-60 md:h-72`.
 *   - Map initialized once the loader resolves; controls minimized.
 *   - Marker created/moved/destroyed in response to `value.latitude`/`value.longitude`.
 *   - Marker `dragend` emits new coords via `onChange`, preserving `text`.
 *   - Cleans up listeners + marker on unmount.
 */

export type AddressPickerMode = 'structured' | 'single';

export interface AddressPickerValue {
  text: string;
  latitude: number | null;
  longitude: number | null;
}

export interface StructuredExtras {
  city: string;
  state: string;
  postalCode: string;
  country: string; // ISO-3166 alpha-2
}

export interface AddressPickerProps {
  mode: AddressPickerMode;
  value: AddressPickerValue;
  onChange: (next: AddressPickerValue, extras?: StructuredExtras) => void;
  placeholder?: string;
  inputId?: string;
  hasError?: boolean;
  disabled?: boolean;
  showMap?: boolean;
  defaultCenter?: { lat: number; lng: number };
}

type LoaderStatus = 'loading' | 'ready' | 'unavailable';

const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;
const DEFAULT_MAP_ZOOM = 15;
const SEOUL_CENTER: LatLngLiteral = { lat: 37.5665, lng: 126.978 };

// Minimal typing mirroring `placeParser.ts` / `mapsLoader.ts` — we don't ship
// `@types/google.maps`, so we describe only the shape we touch.
interface PredictionLike {
  place_id?: string;
  description?: string;
}

interface AutocompleteServiceLike {
  getPlacePredictions(
    request: {
      input: string;
      sessionToken?: unknown;
      language?: string;
      region?: string;
    },
    callback: (results: PredictionLike[] | null, status: string) => void,
  ): void;
}

interface PlacesServiceLike {
  getDetails(
    request: {
      placeId: string;
      fields: string[];
      sessionToken?: unknown;
    },
    callback: (result: unknown, status: string) => void,
  ): void;
}

interface LatLngLiteral {
  lat: number;
  lng: number;
}

interface LatLngLike {
  lat: () => number;
  lng: () => number;
}

interface MapsEventListenerLike {
  remove: () => void;
}

interface MapLike {
  setCenter: (latLng: LatLngLiteral | LatLngLike) => void;
  setZoom: (zoom: number) => void;
  panTo: (latLng: LatLngLiteral | LatLngLike) => void;
}

interface MarkerLike {
  setPosition: (latLng: LatLngLiteral | LatLngLike) => void;
  getPosition: () => LatLngLike | null | undefined;
  setMap: (map: MapLike | null) => void;
  addListener: (event: string, handler: () => void) => MapsEventListenerLike;
}

interface GeocoderLike {
  geocode(
    request: {
      address: string;
      region?: string;
      language?: string;
    },
    callback: (
      results: Array<{
        geometry?: {
          location?: { lat: () => number; lng: () => number };
        };
      }> | null,
      status: string,
    ) => void,
  ): void;
}

interface MapsNamespaceLike {
  Map: new (el: HTMLElement, options: Record<string, unknown>) => MapLike;
  Marker: new (options: Record<string, unknown>) => MarkerLike;
  Geocoder: new () => GeocoderLike;
  places: {
    AutocompleteService: new () => AutocompleteServiceLike;
    AutocompleteSessionToken: new () => unknown;
    PlacesService: new (attr: HTMLDivElement) => PlacesServiceLike;
  };
}

// Subset of `google.maps.places.PlacesServiceStatus` / `AutocompleteService` status.
const STATUS_OK = 'OK';

const AddressPicker: React.FC<AddressPickerProps> = ({
  mode,
  value,
  onChange,
  placeholder,
  inputId,
  hasError = false,
  disabled = false,
  showMap = true,
  defaultCenter,
}) => {
  const { t, i18n } = useTranslation('profile');
  const [status, setStatus] = useState<LoaderStatus>('loading');
  const [predictions, setPredictions] = useState<PredictionLike[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Keep the latest value in a ref so callbacks don't capture stale state.
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // Refs for Google services. Populated once the loader resolves.
  const autocompleteServiceRef = useRef<AutocompleteServiceLike | null>(null);
  const placesServiceRef = useRef<PlacesServiceLike | null>(null);
  const geocoderRef = useRef<GeocoderLike | null>(null);
  const sessionTokenRef = useRef<unknown>(null);
  const placesAttachRef = useRef<HTMLDivElement | null>(null);

  // Tracks whether we've already attempted a one-shot geocode on hydrated
  // text, so we don't loop if the address is genuinely ungeocodable.
  const hydrationGeocodeAttemptedRef = useRef(false);

  // Autocomplete + map hook in from 5.3 — map ref lives here.
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<MapLike | null>(null);
  const markerRef = useRef<MarkerLike | null>(null);
  const markerListenerRef = useRef<MapsEventListenerLike | null>(null);

  // Debounce timer for autocomplete requests.
  const debounceTimerRef = useRef<number | null>(null);
  // Track outstanding requests so late responses don't overwrite fresh ones.
  const latestRequestIdRef = useRef(0);

  const language: 'ko' | 'en' = useMemo(
    () => (i18n.language?.startsWith('ko') ? 'ko' : 'en'),
    [i18n.language],
  );
  const region = language === 'ko' ? 'KR' : undefined;

  const resolvedDefaultCenter = useMemo<LatLngLiteral>(
    () => defaultCenter ?? SEOUL_CENTER,
    [defaultCenter],
  );

  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    if (!apiKey) {
      console.warn(
        '[AddressPicker] REACT_APP_GOOGLE_MAPS_API_KEY is not defined — map disabled',
      );
      setStatus('unavailable');
      return;
    }

    let cancelled = false;
    loadGoogleMaps({ apiKey, language, region })
      .then((google) => {
        if (cancelled) return;
        try {
          const mapsNs = (google as unknown as {
            maps: MapsNamespaceLike;
          }).maps;
          const places = mapsNs.places;

          autocompleteServiceRef.current = new places.AutocompleteService();
          sessionTokenRef.current = new places.AutocompleteSessionToken();

          // Attach PlacesService to a throwaway hidden div (Google best practice)
          // so it's independent of the optional map instance.
          if (!placesAttachRef.current) {
            placesAttachRef.current = document.createElement('div');
            placesAttachRef.current.style.display = 'none';
          }
          placesServiceRef.current = new places.PlacesService(placesAttachRef.current);

          // Geocoder powers the "resolve free-text address to a pin" path
          // (onBlur + initial hydration).
          geocoderRef.current = new mapsNs.Geocoder();

          setStatus('ready');
        } catch (err) {
          console.warn('[AddressPicker] Failed to initialize Places services', err);
          setStatus('unavailable');
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof MapsLoaderError) {
          console.warn(`[AddressPicker] Google Maps unavailable: ${err.code}`);
        } else {
          console.warn('[AddressPicker] Google Maps unavailable', err);
        }
        setStatus('unavailable');
      });

    return () => {
      cancelled = true;
    };
    // Re-run when language changes so the script loads with the right locale.
    // The loader caches its in-flight Promise, so this is cheap.
  }, [language, region]);

  // Clean up the debounce timer on unmount.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Initialize the map once the loader resolves and the map container is in
  // the DOM. Idempotent: only creates the Map instance on the first pass.
  useEffect(() => {
    if (!showMap || status !== 'ready') return;
    if (mapInstanceRef.current) return;
    if (!mapRef.current) return;

    const win = window as unknown as { google?: { maps?: MapsNamespaceLike } };
    const maps = win.google?.maps;
    if (!maps) return;

    const hasCoords =
      typeof value.latitude === 'number' && typeof value.longitude === 'number';
    const center: LatLngLiteral = hasCoords
      ? { lat: value.latitude as number, lng: value.longitude as number }
      : resolvedDefaultCenter;

    try {
      mapInstanceRef.current = new maps.Map(mapRef.current, {
        center,
        zoom: DEFAULT_MAP_ZOOM,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
      });

      if (hasCoords) {
        const marker = new maps.Marker({
          position: center,
          map: mapInstanceRef.current,
          draggable: true,
        });
        markerListenerRef.current = marker.addListener('dragend', () => {
          const pos = marker.getPosition();
          if (!pos) return;
          const prev = valueRef.current;
          onChange({
            text: prev.text,
            latitude: pos.lat(),
            longitude: pos.lng(),
          });
        });
        markerRef.current = marker;
      }
    } catch (err) {
      console.warn('[AddressPicker] Failed to initialize map', err);
    }
    // Intentionally only depends on `showMap` and `status`. Coordinate changes
    // are handled by the sync effect below so we don't re-create the map on
    // every value update.
    // eslint-disable-next-line
  }, [showMap, status]);

  // Sync marker + map center with `value.latitude`/`value.longitude`.
  // Runs when coords change (autocomplete selection, parent hydration, or
  // after a marker drag updates parent state).
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const win = window as unknown as { google?: { maps?: MapsNamespaceLike } };
    const maps = win.google?.maps;
    if (!maps) return;

    const hasCoords =
      typeof value.latitude === 'number' && typeof value.longitude === 'number';

    if (!hasCoords) {
      // No coordinates — drop the marker and recenter on the default.
      if (markerListenerRef.current) {
        markerListenerRef.current.remove();
        markerListenerRef.current = null;
      }
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      map.setCenter(resolvedDefaultCenter);
      return;
    }

    const nextPos: LatLngLiteral = {
      lat: value.latitude as number,
      lng: value.longitude as number,
    };

    map.panTo(nextPos);

    if (markerRef.current) {
      markerRef.current.setPosition(nextPos);
    } else {
      try {
        const marker = new maps.Marker({
          position: nextPos,
          map,
          draggable: true,
        });
        markerListenerRef.current = marker.addListener('dragend', () => {
          const pos = marker.getPosition();
          if (!pos) return;
          const prev = valueRef.current;
          onChange({
            text: prev.text,
            latitude: pos.lat(),
            longitude: pos.lng(),
          });
        });
        markerRef.current = marker;
      } catch (err) {
        console.warn('[AddressPicker] Failed to create marker', err);
      }
    }
  }, [value.latitude, value.longitude, onChange, resolvedDefaultCenter]);

  // Tear down map listeners + marker on unmount.
  useEffect(() => {
    return () => {
      if (markerListenerRef.current) {
        markerListenerRef.current.remove();
        markerListenerRef.current = null;
      }
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      mapInstanceRef.current = null;
    };
  }, []);

  const closeDropdown = useCallback(() => {
    setPredictions([]);
    setHighlightedIndex(-1);
  }, []);

  const refreshSessionToken = useCallback(() => {
    const win = window as unknown as {
      google?: {
        maps?: {
          places?: { AutocompleteSessionToken?: new () => unknown };
        };
      };
    };
    const Ctor = win.google?.maps?.places?.AutocompleteSessionToken;
    sessionTokenRef.current = Ctor ? new Ctor() : null;
  }, []);

  // Resolve the free-text address to lat/lng using the Geocoder, without
  // changing the displayed text. Used on blur and for initial hydration when
  // the saved profile has text but no stored coordinates.
  const geocodeAddress = useCallback(
    (address: string) => {
      const geocoder = geocoderRef.current;
      if (!geocoder) return;
      if (!address || address.trim().length < MIN_QUERY_LENGTH) return;

      geocoder.geocode(
        { address, region, language },
        (results, apiStatus) => {
          if (process.env.NODE_ENV !== 'production') {
            console.debug('[AddressPicker] geocode ->', {
              address,
              apiStatus,
              resultCount: results?.length ?? 0,
            });
          }

          if (apiStatus !== STATUS_OK || !results || results.length === 0) {
            return;
          }

          const loc = results[0].geometry?.location;
          if (!loc || typeof loc.lat !== 'function' || typeof loc.lng !== 'function') {
            return;
          }

          const prev = valueRef.current;
          onChange({
            text: prev.text, // keep what the user typed
            latitude: loc.lat(),
            longitude: loc.lng(),
          });
        },
      );
    },
    [language, onChange, region],
  );

  // One-shot geocode on hydration: if the loaded profile has address text but
  // no saved coordinates, try to resolve them so the pin appears automatically
  // without forcing the user to re-pick from the autocomplete dropdown.
  useEffect(() => {
    if (status !== 'ready') return;
    if (hydrationGeocodeAttemptedRef.current) return;
    if (!value.text || value.text.trim().length < MIN_QUERY_LENGTH) return;
    if (value.latitude !== null || value.longitude !== null) return;

    hydrationGeocodeAttemptedRef.current = true;
    geocodeAddress(value.text);
  }, [status, value.text, value.latitude, value.longitude, geocodeAddress]);

  const runAutocomplete = useCallback(
    (input: string) => {
      const service = autocompleteServiceRef.current;
      if (!service) return;

      const requestId = ++latestRequestIdRef.current;
      service.getPlacePredictions(
        {
          input,
          sessionToken: sessionTokenRef.current ?? undefined,
          language,
          region,
        },
        (results, apiStatus) => {
          // Drop stale responses.
          if (requestId !== latestRequestIdRef.current) return;

          if (process.env.NODE_ENV !== 'production') {
            console.debug('[AddressPicker] getPlacePredictions ->', {
              input,
              apiStatus,
              resultCount: results?.length ?? 0,
            });
          }

          if (apiStatus !== STATUS_OK || !results) {
            // ZERO_RESULTS isn't really an error — just no predictions.
            if (apiStatus === 'ZERO_RESULTS') {
              setPredictions([]);
              setHighlightedIndex(-1);
              setSearchError(null);
              return;
            }
            setPredictions([]);
            setHighlightedIndex(-1);
            setSearchError(t('addressPicker.searchFailed'));
            return;
          }

          setSearchError(null);
          setPredictions(results);
          setHighlightedIndex(results.length > 0 ? 0 : -1);
        },
      );
    },
    [language, region, t],
  );

  const scheduleAutocomplete = useCallback(
    (input: string) => {
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      if (input.trim().length < MIN_QUERY_LENGTH) {
        closeDropdown();
        return;
      }

      // Guard on the ref, not the `status` closure, so keystrokes that arrive
      // slightly before the loader resolves still kick off autocomplete as
      // soon as the services attach. A state-only guard would race and drop
      // early keystrokes silently.
      if (!autocompleteServiceRef.current) {
        if (process.env.NODE_ENV !== 'production') {
          console.debug(
            '[AddressPicker] Autocomplete skipped: service not ready yet',
            { input, status },
          );
        }
        return;
      }

      debounceTimerRef.current = window.setTimeout(() => {
        debounceTimerRef.current = null;
        runAutocomplete(input);
      }, DEBOUNCE_MS);
    },
    [closeDropdown, runAutocomplete, status],
  );

  const handleTextChange = useCallback(
    (nextText: string) => {
      const prev = valueRef.current;
      // Preserve existing coordinates — a keystroke doesn't clear the pin.
      onChange({
        text: nextText,
        latitude: prev.latitude,
        longitude: prev.longitude,
      });
      scheduleAutocomplete(nextText);
    },
    [onChange, scheduleAutocomplete],
  );

  const handleSelectPrediction = useCallback(
    (prediction: PredictionLike) => {
      const service = placesServiceRef.current;
      const placeId = prediction.place_id;

      if (!service || !placeId) {
        setSearchError(t('addressPicker.searchFailed'));
        return;
      }

      // Close the dropdown immediately; the details fetch is async.
      closeDropdown();

      service.getDetails(
        {
          placeId,
          fields: ['address_components', 'formatted_address', 'geometry'],
          sessionToken: sessionTokenRef.current ?? undefined,
        },
        (result, apiStatus) => {
          if (apiStatus !== STATUS_OK || !result) {
            setSearchError(t('addressPicker.searchFailed'));
            return;
          }

          setSearchError(null);

          const parsed = parsePlace(
            result as Parameters<typeof parsePlace>[0],
          );

          if (mode === 'structured') {
            onChange(
              {
                text: parsed.addressLine1,
                latitude: parsed.latitude,
                longitude: parsed.longitude,
              },
              {
                city: parsed.city,
                state: parsed.state,
                postalCode: parsed.postalCode,
                country: parsed.country,
              },
            );
          } else {
            onChange({
              text: parsed.formattedAddress,
              latitude: parsed.latitude,
              longitude: parsed.longitude,
            });
          }

          // Billing efficiency: one session = one autocomplete burst + one
          // getDetails call. Refresh for the next search.
          refreshSessionToken();
        },
      );
    },
    [closeDropdown, mode, onChange, refreshSessionToken, t],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (predictions.length === 0) {
        if (e.key === 'Escape') {
          closeDropdown();
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((idx) =>
            idx + 1 >= predictions.length ? 0 : idx + 1,
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((idx) =>
            idx <= 0 ? predictions.length - 1 : idx - 1,
          );
          break;
        case 'Enter':
          if (highlightedIndex >= 0 && highlightedIndex < predictions.length) {
            e.preventDefault();
            handleSelectPrediction(predictions[highlightedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          closeDropdown();
          break;
        default:
          break;
      }
    },
    [closeDropdown, handleSelectPrediction, highlightedIndex, predictions],
  );

  const inputClass = [
    'w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:outline-none',
    hasError
      ? 'border-red-500 focus:ring-red-500'
      : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500',
    disabled ? 'bg-gray-100 cursor-not-allowed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const resolvedPlaceholder = placeholder ?? t('addressPicker.placeholder');

  return (
    <div className="space-y-2">
      <div className="relative">
        <input
          id={inputId}
          type="text"
          value={value.text}
          onChange={(e) => handleTextChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            // Delay slightly so a click on a prediction fires before blur closes it.
            window.setTimeout(closeDropdown, 150);
            // If the user typed an address but never picked an autocomplete
            // result, try to geocode what they typed so the pin still drops.
            // Only runs when there are no coords yet — otherwise the existing
            // pin stays put.
            const current = valueRef.current;
            if (
              current.text &&
              current.text.trim().length >= MIN_QUERY_LENGTH &&
              current.latitude === null &&
              current.longitude === null
            ) {
              geocodeAddress(current.text);
            }
          }}
          placeholder={resolvedPlaceholder}
          disabled={disabled}
          className={inputClass}
          aria-invalid={hasError || undefined}
          aria-autocomplete="list"
          aria-expanded={predictions.length > 0 || undefined}
          autoComplete="off"
          role="combobox"
        />

        {predictions.length > 0 && (
          <ul
            role="listbox"
            className="absolute left-0 right-0 top-full mt-1 z-50 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg"
          >
            {predictions.map((prediction, index) => {
              const isHighlighted = index === highlightedIndex;
              return (
                <li
                  key={prediction.place_id ?? `${index}-${prediction.description ?? ''}`}
                  role="option"
                  aria-selected={isHighlighted}
                  className={[
                    'px-3 py-2 text-sm cursor-pointer',
                    isHighlighted ? 'bg-blue-50 text-blue-900' : 'text-gray-700 hover:bg-gray-50',
                  ].join(' ')}
                  // Use onMouseDown so the selection fires before the input's blur closes the list.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelectPrediction(prediction);
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {prediction.description}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {searchError && (
        <p className="text-sm text-red-600" role="alert">
          {searchError}
        </p>
      )}

      {showMap && status === 'loading' && (
        <div
          className="bg-gray-200 animate-pulse h-60 md:h-72 rounded"
          role="status"
          aria-label={t('addressPicker.loading')}
        />
      )}

      {showMap && status === 'unavailable' && (
        <p className="text-sm text-gray-500">{t('addressPicker.unavailable')}</p>
      )}

      {showMap && status === 'ready' && (
        <div
          ref={mapRef}
          className="w-full h-60 md:h-72 rounded"
          role="application"
          aria-label={t('addressPicker.placeholder')}
        />
      )}
    </div>
  );
};

export default AddressPicker;
